/**
 * AnimationHero class handles the reveal animation of hero block elements.
 * Animations run only on desktop (min-width: 992px) and if user does not prefer reduced motion.
 * On mobile or reduced motion, all elements are shown immediately.
 */
class AnimationHero {

	static ANIMATION_DELAYS = {
		contentColumn: 400,
		logo: 100,
		imagesColumn: 1400,
		image1: 1700,
		image2: 1900,
	};

	static SELECTORS = [
		".b-animation-hero__content-column",
		".b-animation-hero__logo",
		".b-animation-hero__images-column",
		".b-animation-hero__image",
		".b-animation-hero__image2",
	];

	/**
	 * @param {HTMLElement} block - The main .b-animation-hero block element
	 */
	constructor( block ) {

		this.block = block;
		this.breakpoint = 992;
		this.mediaQuery = window.matchMedia( `(min-width:${this.breakpoint}px)` );
		this.prefersReducedMotionQuery = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		);
		this.prefersReducedMotion = this.prefersReducedMotionQuery.matches;
		this.timeouts = [];

		this.initElements();
		this.handleMediaQuery = this.handleMediaQuery.bind( this );
		this.mediaQuery.addEventListener( "change", this.handleMediaQuery );
		this.prefersReducedMotionQuery.addEventListener(
			"change",
			this.handleMediaQuery,
		);

		this.observer = new MutationObserver( ( mutations ) => {

			mutations.forEach( ( mutation ) => {

				mutation.removedNodes.forEach( ( node ) => {

					if ( node === this.block ) {

						this.destroy();
						this.observer.disconnect();

					}

				} );

			} );

		} );
		if ( this.block.parentNode ) {

			this.observer.observe( this.block.parentNode, { childList: true } );

		}

		if ( this.shouldAnimate() ) {

			this.runAnimations();

		} else {

			this.showAllImmediately();

		}

	}

	/**
	 * Determines if animations should run (desktop and no reduced motion).
	 * @return {boolean} True if animations should run, false otherwise.
	 */
	shouldAnimate() {

		return this.mediaQuery.matches && !this.prefersReducedMotion;

	}

	/**
	 * Initializes references to block elements.
	 * Throws error if any required element is missing.
	 */
	initElements() {

		this.elements = AnimationHero.SELECTORS.map( ( sel ) =>
			this.block.querySelector( sel ),
		);

		const missing = this.elements
			.map( ( el, idx ) => ( el ? null : AnimationHero.SELECTORS[idx] ) )
			.filter( Boolean );

		if ( missing.length > 0 ) {

			// eslint-disable-next-line no-console
			console.warn(
				`AnimationHero: Missing elements: ${missing.join( ", " )}. Animations will skip these.`,
			);

		}

		[
			this.contentColumn,
			this.logo,
			this.imagesColumn,
			this.image1,
			this.image2,
		] = this.elements;

		this.elements = this.elements.filter( Boolean );

	}

	/**
	 * Handles media query changes (desktop <-> mobile and reduced motion).
	 */
	handleMediaQuery() {

		this.prefersReducedMotion = this.prefersReducedMotionQuery.matches;
		if ( this.shouldAnimate() ) {

			this.resetVisibility();
			this.runAnimations();

		} else {

			this.showAllImmediately();

		}

	}

	/**
	 * Runs reveal animations for hero block elements (desktop only).
	 */
	runAnimations() {

		this.timeouts.forEach( clearTimeout );
		this.timeouts = [];
		const steps = [
			{
				el: this.contentColumn,
				delay: AnimationHero.ANIMATION_DELAYS.contentColumn,
			},
			{ el: this.logo, delay: AnimationHero.ANIMATION_DELAYS.logo },
			{
				el: this.imagesColumn,
				delay: AnimationHero.ANIMATION_DELAYS.imagesColumn,
			},
			{ el: this.image1, delay: AnimationHero.ANIMATION_DELAYS.image1 },
			{ el: this.image2, delay: AnimationHero.ANIMATION_DELAYS.image2 },
		];
		steps.forEach( ( { el, delay } ) => {

			if ( !el ) {

				return;

			}

			el.classList.remove( "is-visible" );

			const t = setTimeout( () => el.classList.add( "is-visible" ), delay );
			this.timeouts.push( t );

		} );

	}

	/**
	 * Instantly shows all hero block elements (mobile or reduced motion).
	 */
	showAllImmediately() {

		this.timeouts.forEach( clearTimeout );
		this.timeouts = [];
		this.elements.forEach( ( el ) => {

			el.classList.add( "is-visible" );

		} );

	}

	/**
	 * Resets visibility of all hero block elements.
	 */
	resetVisibility() {

		this.timeouts.forEach( clearTimeout );
		this.timeouts = [];
		this.elements.forEach( ( el ) => {

			el.classList.remove( "is-visible" );

		} );

	}

	/**
	 * Removes event listeners and shows all elements.
	 */
	destroy() {

		this.mediaQuery.removeEventListener( "change", this.handleMediaQuery );
		this.prefersReducedMotionQuery.removeEventListener(
			"change",
			this.handleMediaQuery,
		);
		this.timeouts.forEach( clearTimeout );
		this.timeouts = [];
		this.showAllImmediately();

	}

}

// Initialize AnimationHero for each block on the page
document.querySelectorAll( ".b-animation-hero" ).forEach( ( block ) => {

	new AnimationHero( block );

} );
